import { Plus as PlusIcon } from "lucide-react";
import * as React from "react";

import { CreateCollection } from "@/shared/components/collections/CreateCollection";
import {
  RobotoButton,
  type RobotoButtonVariant,
} from "@/shared/foundation/primitives/RobotoButton";

import styles from "./CreateCollectionButton.module.css";

interface CreateCollectionButtonProps {
  label?: string;
  /**
   * Trigger style: the solid magenta CTA, or the neutral outlined one used by
   * the empty state. Passed straight through to {@link RobotoButton}.
   */
  variant?: RobotoButtonVariant;
  menuOrigin?: "left" | "center" | "right";
}

export function CreateCollectionButton({
  label = "New Collection",
  variant = "primary",
  menuOrigin = "right",
}: CreateCollectionButtonProps) {
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const handleClose = React.useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const handleMouseDown = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open, handleClose]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleClose]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    // CreateCollection's Name field already has autoFocus, so this is usually
    // a no-op — but if that ever changes, fall back to focusing the panel
    // itself so keyboard users aren't left stranded on the trigger button.
    if (!panelRef.current?.contains(document.activeElement)) {
      panelRef.current?.focus();
    }
  }, [open]);

  const panelClassMap: Record<typeof menuOrigin, string> = {
    left: styles.panelLeft,
    center: styles.panelCenter,
    right: styles.panelRight,
  };
  const panelClass = panelClassMap[menuOrigin];

  const toggle = () => setOpen((v) => !v);

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <RobotoButton
        ref={triggerRef}
        variant={variant}
        icon={<PlusIcon aria-hidden="true" />}
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {label}
      </RobotoButton>
      {open && (
        <div
          ref={panelRef}
          className={`${styles.panel} ${panelClass}`}
          role="dialog"
          aria-label="Create collection"
          tabIndex={-1}
        >
          <CreateCollection close={handleClose} />
        </div>
      )}
    </div>
  );
}
